/**
 * Partners Section Component
 * Display of trusted partners and sponsors - fetched from CMS API
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';

const CMS_API_URL = process.env.REACT_APP_API_BASE_URL_CMS || '';
const CATEGORY_ID = 35;

const PartnersSection = () => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch partners from CMS API
  useEffect(() => {
    const fetchPartners = async () => {
      try {
        const response = await axios.post(
          `${CMS_API_URL}info/by-category`,
          { CategoryId: CATEGORY_ID },
          {
            headers: { 'Content-Type': 'application/json' },
            timeout: 30000,
          }
        );

        if (response.data?.code === 1 && Array.isArray(response.data?.data)) {
          // Filter: only show items that are NOT deleted AND are published
          const filteredItems = response.data.data.filter(
            (item) => item.IsDeleted === false && item.IsPublished === true
          );

          const apiPartners = filteredItems.map((item, index) => ({
            id: item.InfoId || index,
            name: item.Title || '',
            logo: item.ImageUrl || item.Image || '',
          }));

          setPartners(apiPartners);
        }
      } catch (error) {
        console.error('Failed to fetch partners:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPartners();
  }, []);

  if (loading) {
    return (
      <section className="pt-3 bg-white">
        <div className="container mx-auto px-6 md:px-8 lg:px-12 pb-7">
          <div className="text-center mb-10">
            <div className="h-8 w-48 bg-[#2f3951]/20 rounded mx-auto animate-pulse" />
            <div className="w-24 h-1 bg-[#ca9a37]/50 mx-auto mt-3 animate-pulse" />
          </div>
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="w-24 h-24 md:w-32 md:h-32 bg-gray-100 rounded-full animate-pulse" />
            ))}
          </div>
        </div>
        <div className="flex">
          <div className="flex-1 h-1 bg-[#2f3951]" />
          <div className="flex-1 h-1 bg-[#ca9a37]" />
          <div className="flex-1 h-1 bg-[#2f3951]" />
          <div className="flex-1 h-1 bg-[#ca9a37]" />
        </div>
      </section>
    );
  }

  if (partners.length === 0) {
    return null;
  }

  return (
    <section className="pt-3 bg-white">
      <div className="container mx-auto px-6 md:px-8 lg:px-12 pb-7">
        {/* Section Header */}
        <div className="text-center mb-10">
          <h2 className="text-2xl md:text-3xl font-bold text-[#2f3951]">
            Trusted Partners
          </h2>
          <div className="w-24 h-1 bg-[#ca9a37] mx-auto mt-3"></div>
        </div>

        {/* Partners Grid */}
        <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16">
          {partners.map((partner) => (
            <div
              key={partner.id}
              className="group flex flex-col items-center"
            >
              <div className="w-24 h-24 md:w-32 md:h-32 bg-gray-50 rounded-full flex items-center justify-center border border-gray-200 group-hover:border-[#ca9a37] group-hover:shadow-lg transition-all p-3 md:p-4 overflow-hidden">
                <img
                  src={partner.logo}
                  alt={`${partner.name} logo`}
                  className="w-full h-full object-contain"
                  loading="lazy"
                />
              </div>
              <p className="mt-3 text-sm text-gray-600 text-center font-medium">
                {partner.name}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Decorative Border */}
      <div className="flex">
        <div className="flex-1 h-1 bg-[#2f3951]"></div>
        <div className="flex-1 h-1 bg-[#ca9a37]"></div>
        <div className="flex-1 h-1 bg-[#2f3951]"></div>
        <div className="flex-1 h-1 bg-[#ca9a37]"></div>
      </div>
    </section>
  );
};

export default PartnersSection;
